import React, { useState } from 'react';
import { 
  MoreVertical, 
  Eye, 
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  Calendar,
  DollarSign,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
  ShoppingBag
} from 'lucide-react';
import useAdminStore from '../../../store/adminStore';
import { formatDistanceToNow } from 'date-fns';

const OrderTable = ({ 
  orders, 
  pagination,
  onView, 
  onEdit, 
  onRefresh,
  onPageChange,
  isLoading = false 
}) => {
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.limit || 20);
  
  const { updateOrderAdmin } = useAdminStore();

  const handleStatusUpdate = async () => {
    try {
      await updateOrderAdmin(selectedOrderId, { status: newStatus });
      setShowStatusModal(false);
      setNewStatus('');
      setSelectedOrderId(null);
      setShowActions(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Update order status error:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange && newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    if (onPageChange) {
      onPageChange(1, newLimit);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
      CONFIRMED: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
      PROCESSING: { color: 'bg-purple-50 text-purple-700 border-purple-200', icon: TrendingUp },
      SHIPPED: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Package },
      DELIVERED: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      COMPLETED: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      CANCELLED: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      REFUNDED: { color: 'bg-gray-50 text-gray-700 border-gray-200', icon: XCircle },
    };
    
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color} shadow-neomorph-inset`}>
        <Icon className="h-2.5 w-2.5 mr-0.5" />
        {status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      PAID: 'bg-green-50 text-green-700 border-green-200',
      PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      FAILED: 'bg-red-50 text-red-700 border-red-200',
      REFUNDED: 'bg-gray-50 text-gray-700 border-gray-200',
    };
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusConfig[status] || statusConfig.PENDING} shadow-neomorph-inset`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const total = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const limit = pagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-[#E8ECF5]">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Order #</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Buyer</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Seller</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Business</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Payment</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                    <ShoppingBag className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No orders found</p>
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id} className="hover:bg-[#E8ECF5] transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-[10px] font-mono font-medium text-gray-800 bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
                      {order.orderNumber}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="h-6 w-6 flex-shrink-0">
                        {order.buyer?.avatar ? (
                          <img 
                            src={order.buyer.avatar} 
                            alt={order.buyer.firstName}
                            className="h-6 w-6 rounded-lg object-cover shadow-neomorph-inset"
                          />
                        ) : (
                          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                            <span className="text-white text-[10px] font-semibold">
                              {order.buyer?.firstName?.[0]}{order.buyer?.lastName?.[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-2">
                        <div className="text-xs font-medium text-gray-800">
                          {order.buyer?.firstName} {order.buyer?.lastName}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {order.buyer?.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-800">
                      {order.seller?.firstName} {order.seller?.lastName}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {order.seller?.email}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-800">
                      {order.business?.name}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="text-xs font-bold text-primary-600">
                      {formatAmount(order.totalAmount)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getPaymentStatusBadge(order.paymentStatus)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    <span className="bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
                      {formatDate(order.createdAt)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap relative">
                    <button
                      onClick={() => {
                        setSelectedOrderId(order.id);
                        setShowActions(!showActions);
                      }}
                      className="p-1 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-600"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                    
                    {selectedOrderId === order.id && showActions && (
                      <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-neomorph border border-gray-100 z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            onView(order);
                            setShowActions(false);
                            setSelectedOrderId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5 mr-2 text-primary-600" />
                          View
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedOrderId(order.id);
                            setShowStatusModal(true);
                            setShowActions(false);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Edit className="h-3.5 w-3.5 mr-2 text-blue-600" />
                          Update Status
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!isLoading && orders.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5">
              <label className="text-[10px] text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-0.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-[10px]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[10px] text-gray-500">per page</span>
            </div>

            <div className="text-[10px] text-gray-600">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span> orders
            </div>

            <div className="flex items-center space-x-0.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-6 h-6 rounded-lg text-[10px] font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowStatusModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-4 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30 sm:mx-0">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <div className="flex items-center mb-1">
                    <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2" />
                    <h3 className="text-base font-semibold text-gray-800">Update Status</h3>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="block w-full px-2.5 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="">Select status...</option>
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleStatusUpdate}
                  disabled={isLoading || !newStatus}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-md px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-xs font-medium text-white hover:shadow-lg hover:shadow-blue-500/40 focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50 transition-all"
                >
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-100 shadow-neomorph px-3 py-1.5 bg-white text-xs font-medium text-gray-700 hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default OrderTable;